import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, of, Subject } from 'rxjs';
import { distinctUntilChanged, debounceTime, first, map, switchMap, take, takeUntil } from 'rxjs/operators';

import { ApplicationService, NominationForm, NomineeFileInfo, NomineeInfo, NomineeTypeInfo, NominationWindowInfo, SchoolInfo, SchoolNominationInfo, FileAttachmentInfo, NominationFormSection, NominationSaveForm, NominationSaveFormData} from 'services/application/application.service';
import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { DownloadService, DownloadFormat, HttpMethod } from 'services/download/download.service';
import { ErrorService } from 'services/error/error.service';
import { ScrollService } from 'services/scroll/scroll.service';
import { SettingsService } from 'services/settings/settings.service';
import { SplashService } from 'services/splash/splash.service';
import { UnsavedChanges, UnsavedChangesService } from 'routes/routes.guard';
import { UserService, UserInfo } from 'services/user/user.service';

@Component({
    selector: 'app-nomination-form',
    styleUrls: ['nominationform.component.scss'],
    templateUrl: 'nominationform.component.html',
})

export class NominationFormComponent implements OnDestroy, OnInit, UnsavedChanges {
    private _subrelease: Subject<void> = new Subject<void>();

    AvailableNomineeTypes: NomineeTypeInfo[];
    AvailableSchools: SchoolInfo[];
    AwardInfo: AwardProgramInfo;
    CanWindowOverride: boolean;
    ChangesDetected: boolean;
    CurrentNomination: NomineeInfo;
    CurrentSchool: SchoolInfo;
    CurrentSchoolYear: string;
    FileCount: number;
    FormWindowOpen: boolean;
    IsNominee: boolean;
    LastSaveReady: boolean;
    LoadedFilesInfo: NomineeFileInfo[];
    LoadedFormValue: string;
    LoadedNominationForm: NominationForm;
    LocationNominations: SchoolNominationInfo;
    NominationForm: FormGroup;
    NominationWindows: NominationWindowInfo[];
    NomineeType: NomineeTypeInfo;
    ParamSAPID: string;
    ParamYear: string;
    ParamGradeLevel: string;
    PhotoWindowDeadline: Date;
    PhotoWindowOpen: boolean;
    PhotoWindowNotSet: boolean;
    SelectedTabIndex: number;
    SiteName: string;
    User: UserInfo;
    ViewReady: boolean;

    constructor(
        private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private dialogSvc: DialogService,
        private downloadSvc: DownloadService,
        private errSvc: ErrorService,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private scrollSvc: ScrollService,
        private settingsSvc: SettingsService,
        private splashSvc: SplashService,
        private unsavedChangesSvc: UnsavedChangesService,
        private userSvc: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();

        // Stop tracking.
        this.TakeDownTracking();
    }

    ngOnInit() {
        const self = this;

        self.AvailableNomineeTypes = null;
        self.AvailableSchools = null;
        self.AwardInfo = null;
        self.CanWindowOverride = false;
        self.CurrentNomination = null;
        self.CurrentSchoolYear = null;
        self.FileCount = null;
        self.FormWindowOpen = false;
        self.LocationNominations = null;
        self.LoadedFormValue = null;
        self.NominationWindows = null;
        self.SelectedTabIndex = 0;
        self.SiteName = '';
        self.PhotoWindowNotSet = false;

        // Wait for a non-null user info object to get the page rolling
        self.userSvc.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(u => !!u && !!u.AuthToken)
        ).subscribe(_user => {
            // Set the initial user info object.
			self.User = _user;

            // Set up constant monitoring of the user object to keep it refreshed
            self.userSvc.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(user => {
                // Update the user stored.
                self.User = user;

                // Also update the CanWindowOverride flag.
                self.CanWindowOverride = self.User ? ['SystemAdmin', 'Developer'].includes(self.User.CurrentRole) : false;

                // Set all of the window opening flags so we can track whether the windows are open for submitting form changes
                if (self.NomineeType) {
                    const formWindow = self.NominationWindows.find(
                        w => w.NomineeTypeID === self.NomineeType.NomineeTypeID && w.WindowTypeCode === 'FORM'
                    );

                    self.FormWindowOpen = self.CanWindowOverride || (formWindow && formWindow.Open);

                    const photoWindow = self.NominationWindows.find(
                        w => w.NomineeTypeID === self.NomineeType.NomineeTypeID && w.WindowTypeCode === 'PHOTO'
                    );

                    self.PhotoWindowDeadline = photoWindow.DateEnd;
                    self.PhotoWindowOpen = self.CanWindowOverride || (photoWindow && photoWindow.Open);
                }
			});

			self.settingsSvc.CurrentSchoolYearSubject.pipe(takeUntil(self._subrelease)).subscribe(year => {
				self.CurrentSchoolYear = year;

				// Load the values needed for page success.
				const nomTypesObs = self.appSvc.GetNomineeTypes();
				const awardObs = self.awardSvc.CurrentAwardsSub;
				const nomWinsObs = self.appSvc.GetNominationWindows(year);
				const curSchoolObs = self.settingsSvc.CurrentSchoolSubject;

				combineLatest(nomTypesObs, awardObs, nomWinsObs, curSchoolObs).pipe(takeUntil(self._subrelease),
					map(_pck => {
						return {
							NomineeTypes: _pck[0],
							AwardInfo: _pck[1],
							NominationWindows: _pck[2],
							CurrentSchool: _pck[3]
						};
					})
				).subscribe(packet => {
					self.AvailableNomineeTypes = packet.NomineeTypes || [];
					self.AwardInfo = packet.AwardInfo;
					self.NominationWindows = packet.NominationWindows || [];
					self.CurrentSchool = packet.CurrentSchool;

					// Early exit if no user
					if (!self.User) {
						return;
					}

					self.CanWindowOverride = ['SystemAdmin', 'Developer'].includes(self.User.CurrentRole);

					// How we set up this form largely depends on what parameters are sent to it. If we receive a type,
					// SAPID, and year, we can directly load the indicated nomination & form, and proceed from there.
					// Otherwise, we must use the type only and base it upon the current user's school. We subscribe
					// to the page parameters here so we can reload/refresh the component should the parameters change
					// (such as someone going from one nominee type's form to another type's).
					const availSchoolsObs = self.appSvc.GetAvailableSchools(self.AwardInfo.AwardID, self.CurrentSchoolYear);
					const pageparams = self.route.paramMap;

					combineLatest(availSchoolsObs, pageparams).pipe(takeUntil(self._subrelease),
						switchMap(_pck => {
							return of<PageParametersInfo>(<PageParametersInfo>{
								Schools: _pck[0],
								Params: {
									NomineeType: _pck[1].has('NomineeType') ? +_pck[1].get('NomineeType') : null,
									GradeLevel: _pck[1].has('GradeLevel') ? _pck[1].get('GradeLevel') : null,
									SAPID: _pck[1].get('SAPID'),
									Year: _pck[1].get('Year'),
								}
							});
						})
					).subscribe(ppacket => {
						// Save the available schools
						self.AvailableSchools = ppacket.Schools;

						// Now, break out the parameters we received.
						self.NomineeType =
							ppacket.Params.NomineeType != null
								? self.AvailableNomineeTypes.find(nt => nt.NomineeTypeID === +ppacket.Params.NomineeType)
								: null;
						self.ParamSAPID = ppacket.Params.SAPID;
						self.ParamYear = ppacket.Params.Year;
						self.ParamGradeLevel = ppacket.Params.GradeLevel;

						// Set the form opening flag so we can track whether the window is open for submitting form changes
						if (self.NomineeType) {
							const formWindow = self.NominationWindows.find(
								w => w.NomineeTypeID === self.NomineeType.NomineeTypeID && w.WindowTypeCode === 'FORM'
							);

							self.FormWindowOpen = self.CanWindowOverride || (formWindow && formWindow.Open);

							if (self.NomineeType.AttachmentTypes.find(f => f.Code === 'PHOTO') != null) {
								const photoWindow = self.NominationWindows.find(
									w => w.NomineeTypeID === self.NomineeType.NomineeTypeID && w.WindowTypeCode === 'PHOTO'
								);

								if (photoWindow == null) {
									self.PhotoWindowNotSet = true;
								}
								else {
									self.PhotoWindowDeadline = photoWindow.DateEnd;
									self.PhotoWindowOpen = self.CanWindowOverride || (photoWindow && photoWindow.Open);
								}
							}
						}

						// Complete the page loading by getting the nominee information, including the form.
						(self.ParamSAPID && self.ParamYear
							? // This is a single form load by an individual with specific SAPID and Year provided.
							self.appSvc.GetNominationBySAPID(
								self.User.AuthToken,
								self.User.CurrentRole,
								self.AwardInfo.AwardID,
								self.NomineeType.NomineeTypeID,
								self.ParamSAPID,
								self.ParamYear
							)
							: // All we have is the nominee type. We need to get the nominations for the current year
							// and user location, and from that we'll know which form we need to load.
							// However, if the user's current role is a SystemAdmin or Developer, then this will use
							// the current school number, NOT the user's location.
							self.appSvc.GetNominationsByWorkLocation(
								self.User.AuthToken,
								self.CurrentSchool.SchoolNumber,
								self.AwardInfo.AwardID,
								self.CurrentSchoolYear
							).pipe(switchMap(nominations => {
								if (
									self.NomineeType &&
									nominations &&
									nominations.Nominees &&
									!!nominations.Nominees.length
								) {
									// Depending on which type we are tracking, queue that form up for loading.
									const nominee = nominations.Nominees.find(n =>
										n.NomineeTypeID === self.NomineeType.NomineeTypeID &&
										n.GradeLevel == self.ParamGradeLevel
									);
									return of<NomineeInfo>(nominee).pipe(take(1));
								}
								else {
									// No way to get nomination.
									return of<NomineeInfo>(null).pipe(take(1));
								}
							}))
						).pipe(takeUntil(self._subrelease)).subscribe(nominee => {
							// Subscribe to the nominee info we have now available to finish loading the page.
							self.CurrentNomination = nominee;
							self.IsNominee = self.CurrentNomination && self.CurrentNomination.SAPID === self.User.SAPID;

							if (self.CurrentNomination) {
								// Set the site name. If there are any slashes in it (perhaps from a school group name),
								// remove those now.
								self.SiteName = self.CurrentNomination.SiteName.replace(/\s\/\s/g, '_');

								// We have a nominee packet. Load the nomination form for that packet.
								self.appSvc.GetNominationForm(
									self.User.AuthToken,
									self.User.CurrentRole,
									self.CurrentNomination.NominationFormUID
								).pipe(takeUntil(self._subrelease)).subscribe(form => {
									self.LoadedNominationForm = form;
									self.LoadedFilesInfo = form.Files;

									// Now that everything has loaded, we can setup the form groups properly.
									self.SetupNominationFormGroups();

									// Setup change tracking
									self.SetupTracking();

									// Show the page
									self.ViewReady = true;
								},
									ferror => self.errSvc.HandleServiceError(ferror));
							}
							else {
								// No form to load as there's no nominee. Release the view.
								self.ViewReady = true;
							}
						}, error => self.errSvc.HandleServiceError(error));
					});
				}, error => self.errSvc.HandleServiceError(error));
			});
        }, error => self.errSvc.HandleServiceError(error));
    }

    CanDeactivate(liveCheck?: boolean) {
        const self = this;

        if (!self.NominationForm) {
            return true;
        }

        // Are there unsaved changes?
        if (!self.ChangesDetected) {
            // No. The user can deactivate.
            return true;
        }

        // If this is a live check (native from browser), forego the confirmation dialog as the native
        // browser will catch instead.
        if (liveCheck === true) {
            return false;
        }

        // Need to prompt
        return self.dialogSvc.OpenConfirmationDialog({
            TitleText: 'Unsaved Changes Detected',
            MessageText:
                `The nomination form appears to have unsaved changes which have not yet been submitted ` +
                `to the system. If you leave this page now, your unsaved changes will be lost. Do you wish ` +
                `to discard your changes and leave the page?`,
            OKText: 'Yes - Discard Changes',
            CancelText: 'No - Stay On Page',
        });
    }

    ChangeTab(index: number) {
        this.SelectedTabIndex = index;

        // Trigger the scroll event so the master container can scroll the panel back up to view.
        this.scrollSvc.TriggerScroll();
    }

    DownloadFile(item: FileAttachmentInfo) {
        const self = this;

        if (!item) {
            return;
        }

        self.downloadSvc
            .RetrieveDownload(
                HttpMethod.GET,
                'Api/Services/App/DownloadFile',
                {
                    AuthToken: self.User.AuthToken || '',
                    FileName: item.FileName,
                    FilePath: item.FileStorageURL,
                },
                DownloadFormat.PDF
            )
            .pipe(takeUntil(self._subrelease)).subscribe(result => {}, error => self.errSvc.HandleServiceError(error));
    }

    DownloadPDF() {
        const self = this;

        if (!self.CurrentNomination || self.CurrentNomination.NominationFormUID == null) {
            return;
        }

        self.downloadSvc
            .RetrieveDownload(
                HttpMethod.GET,
                'Api/Services/PDF/GetNominationFormPDF',
                {
                    AuthToken: self.User.AuthToken || '',
                    NominationFormUID: self.CurrentNomination.NominationFormUID,
                    Attachment: true,
                },
                DownloadFormat.PDF
            )
            .pipe(takeUntil(self._subrelease)).subscribe(result => {}, error => self.errSvc.HandleServiceError(error));
    }

    FileRemoved(file?: FileAttachmentInfo) {
        const self = this;

        if (!file || !self.NominationForm) {
            return;
        }

        // This is an event that bubbles upward when a file has been removed. Force a recheck to ensure
        // that any relevant file attachment section is checked for completeness after this removal.
        self.UpdateFileAttachmentSection(file);
    }

    FileUploaded(file?: FileAttachmentInfo) {
        const self = this;

        // This is an event that bubbles upward when a file has been uploaded. Force a recheck to ensure
        // that any relevant file attachment section is checked for completeness after this removal.
        const preSaveReady = self.LastSaveReady;

        self.UpdateFileAttachmentSection(file);

        // For our form, we want to check to see if this has now resulted in the form being ready. If so, we
        // can now pop up a congratulations message.
        if (preSaveReady !== true) {
            // We don't think we were ready before this upload. Are we now ready?
            if (self.LastSaveReady === true) {
                // We are now ready. Display the congratulations.
                self.dialogSvc
                    .OpenMessageBoxDialog({
                        TitleText: 'Nomination Form Complete',
                        MessageText:
                            `Congratulations! You've completed your ${self.NomineeType.Description} nomination. ` +
                            `Be sure to upload all supporting files by their respective deadlines. Contact the Public ` +
                            `Relations Department with questions.`,
                        OKText: 'OK',
                    })
                    .pipe(takeUntil(self._subrelease)).subscribe();
            }
        }
    }

    GetFormForTracking(_form?: NominationForm) {
        const self = this;

        if (_form && !self.NominationForm) {
            return null;
        }

        // For change tracking, we want to exclude any section of the form which is marked as a file attachment section.
        // For those, the saving is done automatically when the file is uploaded or removed.
        const form = _form || <NominationForm>self.NominationForm.getRawValue();

        // From the raw form, strip out any sections which are identified as attachment sections.
        form.Sections = (form.Sections || []).filter(s => s.IsAttachment !== true);

        // Now JSON stringify the form we have left for storage.
        return JSON.stringify(form);
    }

    IsFormReady() {
        const self = this;

        if (!self.NominationForm) {
            return false;
        }

        const form = <NominationForm>self.NominationForm.getRawValue();

        if (!form.Sections || !form.Sections.length) {
            return false;
        }

        // Check all sections in turn.
        for (let sec of form.Sections) {
            if (sec.IsAttachment) {
                // This is a file attachment section. To be "ready", there must be at least 1 file uploaded
                // of the type attached to this section.
                if (!sec.AttachmentType) {
                    return false;
                }

                const secAttachType = sec.AttachmentType.AttachmentTypeID;

                // Look for the matching attachment type in the files property.
                const filesInfo = (self.LoadedFilesInfo || []).find(f => f.AttachmentTypeID === secAttachType);

                if (!filesInfo || !filesInfo.Files || !filesInfo.Files.length) {
                    return false;
                }

                // We have one minimum of this type. We're good.
                continue;
            }

            // Check all questions have a non-null, non-empty response.
            if (sec.Questions && sec.Questions.length > 0) {
                for (let question of sec.Questions) {
                    if (question.Required) {
                        if (question.Response == null || question.Response === '' || question.Response === 'Your supervisor will provide this response') {
                            return false;
                        }
                    }
                }
            }
        }

        // If we make it here, all sections are ready.
        return true;
    }

    QuestionRequiresNote(responses: any) {
        if (responses == null) return false;
        if (Array.isArray(responses)) {
            if (responses.filter(r => r.RequiresNote).length) return true;
            return false;
        }
        else if (responses.Value) {
            return (responses.RequiresNote);
        }
        else return false;
    }

    SaveForm(index?: number) {
        const self = this;

        // Check if there are any unsaved changes. If not, no need to save the form; only update the
        // tab index if one was provided, and return.
        if (self.ChangesDetected !== true) {
            if (index != null) {
                let newTab = index + 1;
                if (newTab >= self.LoadedNominationForm.Sections.length) {
                    newTab = 0;
                }

                self.ChangeTab(newTab);
            }

            self.splashSvc.SnackbarAlert('No changes to save.');
            return;
        }

        // There are unsaved changes. Parse through the form and pull out the elements necessary to save the form.
        // Basically, search through each section, then through each question, and retrieve the SectionDetailID,
        // QuestionID, and response text. Save those in an array, then ship it off with the form ID to the service.
        // However, if any particular response has a text limit and it is over the limit, warn about that to the user.
        const formData: NominationSaveFormData[] = [];
        const form = <NominationForm>self.NominationForm.getRawValue();

        for (let i = 0, j = form.Sections.length; i < j; i++) {
            const sec = form.Sections[i];

            if (sec.IsAttachment) {
                continue; // There is nothing to save for attachment sections. Those are handled separately.
            }

            // Iterate through all questions in this section.
            for (let qi = 0, qj = sec.Questions.length; qi < qj; ++qi) {
                let quest = sec.Questions[qi];

                //is it an array (multiple options selected), or a single selection list (value), or just a string
                var response;
                if (Array.isArray(quest.Response)) {
                    response = quest.Response.join('|');
                }
                else if (quest.Response && quest.Response.Value) {
                    response = quest.Response.Value;
                }
                else {
                    response = quest.Response;
                }

                // Package up the FormTemplateSectionID, QuestionID, and response value.
                const answer: NominationSaveFormData = {
                    FormTemplateSectionID: quest.SectionID,
                    FormTemplateSectionDetailID: quest.SectionDetailID,
                    QuestionID: quest.QuestionID,
                    Response: response,
                    OtherResponse: quest.OtherResponse
                };

                //don't send blank answers
                if (response == null || response == "" || response === 'This response has already been provided by your supervisor' || response === 'Your supervisor will provide this response') {
                    continue;
                }

                if (quest.TextLimit != null) {
                    // There's a limit. Compare the size to the limit, and see if we are in violation.
                    if (quest.TextSizeCount > quest.TextLimit) {
                        // The violation is real. Warn the user, and abort the save operation.
                        const message =
                            `A text response to a question on this form is over the required text limit. ` +
                            `Please review the response size for the question shown below and revise the response to fit ` +
                            `within the required text length before saving.<br /><br /><strong>Section: </strong>` +
                            `${sec.Name}<br /><br /><strong>Question: </strong><em>${quest.QuestionText}</em>`;

                        self.dialogSvc.OpenMessageBoxDialog({
                            TitleText: 'Response Too Long',
                            MessageText: message,
                            OKText: 'OK',
                        }).pipe(takeUntil(self._subrelease)).subscribe(() => {
                            // Be kind and move the tab focus to the tab that has the issue.
                            self.ChangeTab(i);
                        });

                        return;
                    }
                }

                if (quest.LowerTextLimit != null) {
                    // There's a limit. Compare the size to the limit, and see if we are in violation.
                    if (quest.TextSizeCount > 0 && quest.TextSizeCount < quest.LowerTextLimit) {
                        // The violation is real. Warn the user, and abort the save operation.

                        var message = 'A text response to a question on this form is under the required text limit. Please review the response size for the question shown below and revise the response to fit within the required text length before saving.<br /><br />';

                        message += '<strong>Section: </strong>' + sec.Name + '<br /><br /><strong>Question: </strong><em>' + quest.QuestionText + '</em>';

                        self.dialogSvc.OpenMessageBoxDialog({
                            TitleText: 'Response Too Short',
                            MessageText: message,
                            OKText: 'OK'
                        }).pipe(takeUntil(self._subrelease)).subscribe(() => {
                            // Be kind and move the tab focus to the tab that has the issue.
                            self.ChangeTab(i);
                        });

                        return;
                    }
                }

                if (quest.DigitOnly) {
                    // There's a validator. Compare and see if we are in violation.
                    if (!/^\d+$/.test(response)) {
                        // The violation is real. Warn the user, and abort the save operation.

                        var message = 'This question requires the answer to only contain numbers. Please review the question and revise your answer to fit the requirements.<br /><br />';

                        message += '<strong>Section: </strong>' + sec.Name + '<br /><br /><strong>Question: </strong><em>' + quest.QuestionText + '</em>' + '<br /><br /><strong>Your Response: </strong><em>' + quest.Response + '</em>';

                        self.dialogSvc.OpenMessageBoxDialog({
                            TitleText: 'Invalid Response',
                            MessageText: message,
                            OKText: 'OK'
                        }).pipe(takeUntil(self._subrelease)).subscribe(() => {
                            // Be kind and move the tab focus to the tab that has the issue.
                            self.ChangeTab(i);
                        });

                        return;
                    }
                }

                if (quest.OtherSelected && quest.OtherResponse == null) {
                    var message = 'You selected a custom response for this question but did not provide your own response. Please review the question and revise your answer to fit the requirements.<br /><br />';

                    message += '<strong>Section: </strong>' + sec.Name + '<br /><br /><strong>Question: </strong><em>' + quest.QuestionText + '</em>' + '<br /><br /><strong>Your Response: </strong><em>' + quest.Options.find(o => o.RequiresNote == true).DisplayText + '</em>' + '<br /><br /><strong>Custom Response: </strong><em>' + quest.OtherResponse + '</em>';

                    self.dialogSvc.OpenMessageBoxDialog({
                        TitleText: 'Custom Response Not Provided',
                        MessageText: message,
                        OKText: 'OK'
                    }).pipe(takeUntil(self._subrelease)).subscribe(() => {
                        // Be kind and move the tab focus to the tab that has the issue.
                        self.ChangeTab(i);
                    });

                    return;
                }

                // Add to the form data array to be saved.
                formData.push(answer);
            }
        }

        this.PackAndPost(formData, form, index);
    }

    PackAndPost(formData: NominationSaveFormData[], form: NominationForm, index: number) {
        let self = this;
        // Create a packet of info to send to the service. This should include the NominationFormUID (so we know which
        // form to save to), and the form data.
        const formToSave: NominationSaveForm = {
            NominationFormUID: form.NominationFormUID,
            FormData: formData,
        };

        // Save the current tab before saving, so we can restore it after the save.
        const currentTab = self.SelectedTabIndex;

        // Save the current "Is Ready" status.
        const preSaveReady = self.LastSaveReady;

        // Call to the service to persist the data. The service will return the updated form in its entirety, since
        // this will update the version number as well.
        self.appSvc.SaveNominationForm(self.userSvc.User.AuthToken, self.userSvc.User.CurrentRole, formToSave).pipe(takeUntil(self._subrelease)).subscribe(
            updatedForm => {
                // The form has been saved successfully. Update the existing form that we've been tracking.
                self.LoadedNominationForm = updatedForm;
                self.LoadedFilesInfo = updatedForm.Files;

                // Ask to sync up the forms.
                self.SetupNominationFormGroups();

                // Splash success.
                self.splashSvc.SnackbarSuccess('Nomination form saved successfully.');

                // If an index was specified in the call, we will advance the index to the next section. Otherwise,
                // preserve the original tab.
                if (index != null) {
                    let newTab = index + 1;
                    if (newTab >= self.LoadedNominationForm.Sections.length) {
                        newTab = 0;
                    }

                    self.ChangeTab(newTab);
                } else {
                    self.ChangeTab(currentTab);
                }

                // Lastly, check to see if the form is now ready. If it is, and it wasn't before the save, pop a confirmation.
                const postIsFormReady = self.LastSaveReady;

                if (postIsFormReady === true && preSaveReady !== true) {
                    // We weren't ready before, we are now. Pop an alert.
                    self.dialogSvc
                        .OpenMessageBoxDialog({
                            TitleText: 'Nomination Form Complete',
                            MessageText:
                                `Congratulations! You've completed your ${self.NomineeType.Description} nomination. ` +
                                `Be sure to upload all supporting files by their respective deadlines. Contact the Public ` +
                                `Relations Department with questions.`,
                            OKText: 'OK',
                        })
                        .pipe(takeUntil(self._subrelease)).subscribe();
                }
            },
            error => self.errSvc.HandleServiceError(error)
        );
    }

    SetupNominationFormGroups() {
        const self = this;

        if (!self.LoadedNominationForm) {
            return;
        }

        const form = self.fb.group({
            NomineeTypeID: [self.LoadedNominationForm.NomineeTypeID, Validators.required],
            NominationFormUID: [self.LoadedNominationForm.NominationFormUID, Validators.required],
            NominationFormVersion: [self.LoadedNominationForm.NominationFormVersion, Validators.required],
            NominationID: [self.LoadedNominationForm.NominationID, Validators.required],
            FormTemplateID: [self.LoadedNominationForm.FormTemplateID, Validators.required],
            DateUpdated: [self.LoadedNominationForm.DateUpdated],
            Deleted: [self.LoadedNominationForm.Deleted],
            FormInstructions: [self.LoadedNominationForm.FormInstructions],
            Sections: [],
            Photos: [],
            // Videos: FileAttachmentInfo[];
        });

        if (self.LoadedNominationForm.Sections && !!self.LoadedNominationForm.Sections.length) {
            const secs = <FormGroup[]>[];

            self.LoadedNominationForm.Sections.forEach(sec => {
                const esec = self.fb.group({
                    SectionID: [sec.SectionID],
                    Name: [sec.Name],
                    RomanNumeral: [sec.RomanNumeral],
                    Instructions: [sec.Instructions],
                    SortOrder: [sec.SortOrder],
                    SectionReady: [sec.SectionReady],
                    IsAttachment: [sec.IsAttachment],
                    AttachmentType: [sec.AttachmentType],
                    PrincipalOnly: [sec.PrincipalOnly],
                    JudgingAvailable: [sec.JudgingAvailable],
                    Questions: [],
                });

                const qarr = <FormGroup[]>[];

                (sec.Questions || []).forEach(q => {
                    const question = self.fb.group({
                        SectionID: [q.SectionID],
                        SectionDetailID: [q.SectionDetailID],
                        QuestionID: [q.QuestionID],
                        QuestionTypeID: [q.QuestionTypeID],
                        QuestionText: [q.QuestionText],
                        Text2: [q.Text2],
                        QuestionNarrativeID: [q.QuestionNarrativeID],
                        NarrativeText: [q.NarrativeText],
                        TextLimit: [q.TextLimit],
                        TextLimitUnit: [q.TextLimitUnit],
                        LowerTextLimit: [q.LowerTextLimit],
                        Response: [q.Response],
                        SortOrder: [q.SortOrder],
                        ShowNumber: [q.ShowNumber],
                        ShowNarrative: [q.ShowNarrative],
                        QuestionNumber: [q.QuestionNumber],
                        DigitOnly: [q.DigitOnly],
                        ResponseInvalid: [q.ResponseInvalid],
                        InitiallyBlank: [q.InitiallyBlank],
                        TextSizeCount: [q.TextSizeCount],
                        OtherResponse: [q.OtherResponse],
                        HasOptions: [q.HasOptions],
                        CanSelectMultiple: [q.CanSelectMultiple],
                        QuestionType: [q.QuestionType],
                        Options: [q.Options],
                        OtherSelected: [q.OtherSelected],
                        FirstName: [q.FirstName],
                        LastName: [q.LastName],
						Required: [q.Required],
						ExpandedText: [q.ExpandedText],
						Inline: [q.Inline]
                    });

                    // Now, depending on certain elements, we need to set up disabled access, subscriptions on changes, etc.
                    if (self.FormWindowOpen !== true || (sec.PrincipalOnly === true && self.IsNominee === true)) {
                        // Disable the response field.
                        question.get('Response').disable();
                    }

                    if (q.QuestionType === 'ShortResponse') {
                        question.get('Response').valueChanges.pipe(
                            takeUntil(self._subrelease),
                            distinctUntilChanged(),
                            debounceTime(250)
                        )
                            .pipe(takeUntil(self._subrelease)).subscribe(value => {
                            if (question.get('DigitOnly').value) {
                                if (/^\d+$/.test(value)) {
                                    question.get('ResponseInvalid').setValue(false, { emitEvent: false });
                                }
                                else {
                                    question.get('ResponseInvalid').setValue(true, { emitEvent: false });
                                }
                            }
                        });
                    }

                    if (q.QuestionType === 'LongResponse') {
                        // This is a text-entry (paragraph) based entry. For those, we also subscribe to the value of the
                        // response entry, so we can update the word counts for the overall formgroup.
                        question.get('Response').valueChanges.pipe(
                            takeUntil(self._subrelease),
                            distinctUntilChanged(),
                            debounceTime(250)
                        )
                        .subscribe(value => {
                            const qval = question.getRawValue();

                            // No need to update counts if there's no limit defined.
                            if (qval.TextLimit == null) {
                                return;
                            }

                            // Depending on the unit (1 = letters, 2 = words), calculate the count now.
                            const text = value || '';

                            let unitcount: number;
                            if (text == null || text.trim() == '') {
                                unitcount = 0;
                            }
                            else if (qval.TextLimitUnit === 1) {
                                unitcount = text.length;
                            }
                            else {
                                unitcount = text.match(/\S+/g).length;
                            }

                            // Update the count in the group
                            question.get('TextSizeCount').setValue(unitcount, { emitEvent: false });
                        });

                        // We also should trigger this updating if there's a response loaded.
						if (q.Response) {
							self.UpdateTextCount(question);
						}
						else {
							question.get('TextSizeCount').setValue(0, { emitEvent: false });
						}
                    }

                    if (q.Response == null || q.Response == "" || q.Response === 'This response has already been provided by your supervisor' || q.Response === 'Your supervisor will provide this response') {
						question.get('InitiallyBlank').setValue(true, { emitEvent: false });
					}

                    if (q.HasOptions) {
                        if (q.CanSelectMultiple) {
                            //value change event
                            question.get('Response').valueChanges.pipe(
                                takeUntil(self._subrelease),
                                distinctUntilChanged(),
                                debounceTime(250)
                            ).subscribe((value: number[]) => {
                                //find the other option, can only have one other per question unless change db or use separators like in response
                                let other = q.Options.find(o => o.RequiresNote == true);

                                //if values include other value
                                if (other && value.includes(other.Value)) {
                                    question.get('OtherSelected').setValue(true, { emitEvent: false });
                                    q.OtherSelected = true;
                                }
                                else {
                                    //if not selected, set OtherSelected to false and clear OtherResponse
                                    question.get('OtherSelected').setValue(false, { emitEvent: false });
                                    q.OtherSelected = false;
                                    question.get('OtherResponse').setValue(null, { emitEvent: false });
                                    q.OtherResponse = null;
                                }
                            });

                            if (q.Response != null && q.Response != "") {
                                let selectedOptions = q.Response.indexOf('|') < 0 ? [+q.Response] : q.Response.split('|').map(x => +x);

                                question.get('Response').setValue(selectedOptions, { emitEvent: false });
                            }
                        }
                        else {
                            //value change event
                            question.get('Response').valueChanges.pipe(
                                takeUntil(self._subrelease),
                                distinctUntilChanged(),
                                debounceTime(250)
                            ).subscribe((value: number) => {
                                //find the other option, can only have one other per question unless change db or use separators like in response
                                let other = q.Options.find(o => o.RequiresNote == true);

                                //if value = other, set OtherSelected to true
                                if (other && other.Value == value) {
                                    question.get('OtherSelected').setValue(true, { emitEvent: false });
                                    q.OtherSelected = true;
                                }
                                else {
                                    //if not selected, set OtherSelected to false and clear OtherResponse
                                    question.get('OtherSelected').setValue(false, { emitEvent: false });
                                    q.OtherSelected = false;
                                    question.get('OtherResponse').setValue(null, { emitEvent: false });
                                    q.OtherResponse = null;
                                }
                            });

                            //convert string to number
                            question.get('Response').setValue(+q.Response, { emitEvent: false });
                        }
                    }

                    qarr.push(question);
                });

                esec.setControl('Questions', self.fb.array(qarr));

                secs.push(esec);
            });

            form.setControl('Sections', self.fb.array(secs));
        }

        // Set up subscription on the form for change detection.
        form.valueChanges
            .pipe(
                takeUntil(self._subrelease),
                debounceTime(250),
                distinctUntilChanged()
            )
            .subscribe(_value => {
                const _jsonvalue = self.GetFormForTracking(<NominationForm>_value);

                self.ChangesDetected = self.LoadedFormValue !== _jsonvalue;
            });

        self.NominationForm = form;

        // Store the form value for change tracking.
        self.StoreFormTrackingInfo();

        // Update the Last Save Ready flag
        self.LastSaveReady = self.IsFormReady();
    }

    SetupTracking() {
        const self = this;

        self.unsavedChangesSvc.SetTracking(self.CanDeactivate.bind(self));
    }

    StoreFormTrackingInfo() {
        const self = this;

        if (!self.NominationForm) {
            return;
        }

        // For form tracking, we want to ignore all sections that are file attachment sections.
        // These sections aren't form data in the sense that they can be saved/submitted. Their actions happen
        // immediately when the user upload/removes files.
        self.LoadedFormValue = self.GetFormForTracking();
        self.ChangesDetected = false;
    }

    TakeDownTracking() {
        this.unsavedChangesSvc.ClearTracking();
    }

    UpdateFileAttachmentSection(file?: FileAttachmentInfo) {
        const self = this;

        if (!file) {
            return;
        }

        // For our form, we need to find any associated file info for the loaded nomination that matches the
        // removed file attachment type, and see if there are still any files remaining uploaded for that type.
        // Then, we can update the SectionReady flag for the section which serves as the entry point for
        // files of that type, but only if they are nominee provided.
        let secReady = true; // Unless otherwise known.

        const fileInfo = (self.LoadedFilesInfo || []).find(f => f.AttachmentTypeID === file.AttachmentTypeID);

        // No file info, or no files in the info, indicates this section is no longer ready.
        secReady = fileInfo && fileInfo.Files && !!fileInfo.Files.length;

        // Now find the section for this file attachment type.
        const form = <NominationForm>self.NominationForm.getRawValue();

        if (!form.Sections || !form.Sections.length) {
            return;
        }

        const secIdx = form.Sections.findIndex(
            s => s.IsAttachment && s.AttachmentType && s.AttachmentType.AttachmentTypeID === file.AttachmentTypeID
        );

        if (secIdx < 0) {
            return;
        } // If there's no section for the attachment type, it likely means the attachment type
        // is not nominee provided, and there's nothing to do here.

        const secarr = <FormArray>self.NominationForm.get('Sections');
        const secGrp = secarr.at(secIdx);

        secGrp.get('SectionReady').setValue(secReady);

        // Trigger a new form readiness check.
        self.LastSaveReady = self.IsFormReady();
    }

    UpdateTextCount(Question: FormGroup) {
        const self = this;

        if (!Question) {
            return;
        }

        const qform = Question.getRawValue();

        if (qform.TextLimit == null) {
            return;
        } // No need to update counts if there's no limit defined.

        // Depending on the unit (1 = letters, 2 = words), calculate the count now.
        let unitcount = 0;

        if (qform.TextLimitUnit === 1) {
            unitcount = (qform.Response || '').length;
        } else {
            // Words, which we define simply as characters separated by at least 1 whitespace.
            const words = (qform.Response || '').match(/\S+/g);
            unitcount = !!words ? words.length : 0;
        }

        // Update the count in the group
        Question.get('TextSizeCount').setValue(unitcount);
    }


}


export class NominationEntryForm {
    NomineeTypeID?: number;
    NominationFormUID: string;
    NominationFormVersion: number;
    NominationID: number;
    FormTemplateID: number;
    DateUpdated: Date;
    Deleted: boolean;
    FormInstructions: string;
    Sections: NominationEntryFormSection[];
    Photos: FileAttachmentInfo[];
    Videos: FileAttachmentInfo[];

    constructor(form: NominationForm) {
        this.NomineeTypeID = form.NomineeTypeID;
        this.NominationFormUID = form.NominationFormUID;
        this.NominationFormVersion = form.NominationFormVersion;
        this.NominationID = form.NominationID;
        this.FormTemplateID = form.FormTemplateID;
        this.DateUpdated = form.DateUpdated;
        this.Deleted = form.Deleted;
        this.FormInstructions = form.FormInstructions;
        this.Photos = form.Photos.slice();
        this.Videos = form.Videos.slice();
    }
}

export class NominationEntryFormSection {
    SectionID: number;
    Name: string;
    RomanNumeral?: string;
    Instructions: string;
    SortOrder: number;
    SectionReady: boolean;
    PrincipalOnly: boolean;
    JudgingAvailable?: boolean;
    AttachmentTypeID: number;
    SizeLimit: number;
    LowerSizeLimit: number;
    Questions: FormArray;
    // RubricQuestions: RubricSectionEntry[];
    // RubricQuestionsScored: number;

    constructor(form: NominationFormSection) {
        this.SectionID = form.SectionID;
        this.Name = form.Name;
        this.RomanNumeral = form.RomanNumeral;
        this.Instructions = form.Instructions;
        this.SortOrder = form.SortOrder;
        this.SectionReady = form.SectionReady;
        this.PrincipalOnly = form.PrincipalOnly;
        this.JudgingAvailable = form.JudgingAvailable;
    }
}

export class PageParametersInfo {
    Schools?: SchoolInfo[];
    Params?: ParametersInfo;
}

export class ParametersInfo {
    NomineeType?: number;
    GradeLevel?: string;
    SAPID?: string;
    Year?: string;
}

